import {
  Check,
  CheckCircle2,
  ExternalLink,
  Globe2,
  Image as ImageIcon,
  MessageCircleMore,
  Play,
  QrCode,
  RefreshCcw,
  Send,
  Sparkles,
  WandSparkles,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

const LAST_STEP = 6;
const STEP_TIMINGS = [650, 1550, 2850, 4250, 5650, 7050];

const demoBenefits = [
  { icon: MessageCircleMore, title: "Mulai dengan #CSJW", text: "Bot membuka sesi dan meminta data wajib." },
  { icon: ImageIcon, title: "Kirim data dan foto", text: "Nama usaha, produk, harga, foto, dan QRIS." },
  { icon: Globe2, title: "Website langsung siap", text: "Link muncul setelah data lengkap dan tervalidasi." },
];

function getPhase(step: number) {
  if (step < 2) return { label: "Mulai chat", index: 0 };
  if (step < 4) return { label: "Lengkapi data", index: 1 };
  if (step < 6) return { label: "Website disiapkan", index: 2 };
  return { label: "Website online", index: 3 };
}

export function ChatDemoSection() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const chatScrollRef = useRef<HTMLDivElement | null>(null);
  const visibilityRef = useRef(false);
  const [inView, setInView] = useState(false);
  const [runId, setRunId] = useState(0);
  const [step, setStep] = useState(0);

  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const visible = entry.isIntersecting && entry.intersectionRatio >= 0.24;

        if (visible && !visibilityRef.current) {
          visibilityRef.current = true;
          setInView(true);
          setRunId((current) => current + 1);
        } else if (!entry.isIntersecting || entry.intersectionRatio < 0.06) {
          visibilityRef.current = false;
          setInView(false);
          setStep(0);
        }
      },
      { threshold: [0, 0.06, 0.24, 0.5, 0.8], rootMargin: "-4% 0px -8% 0px" },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!inView) return;

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    setStep(0);

    if (reducedMotion) {
      setStep(LAST_STEP);
      return;
    }

    const timers = STEP_TIMINGS.map((delay, index) => window.setTimeout(() => setStep(index + 1), delay));
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [inView, runId]);

  useEffect(() => {
    const node = chatScrollRef.current;
    if (!node) return;
    node.scrollTo({ top: node.scrollHeight, behavior: "smooth" });
  }, [step]);

  const phase = useMemo(() => getPhase(step), [step]);
  const progress = Math.max(4, Math.round((step / LAST_STEP) * 100));

  const replay = () => {
    setStep(0);
    setRunId((current) => current + 1);
  };

  return (
    <section ref={sectionRef} id="demo-chat" className="relative overflow-hidden py-20 sm:py-24">
      <div className="absolute inset-0 bg-[linear-gradient(180deg,#f2fbf5_0%,#fbfcfa_54%,#fff7f0_100%)]" />
      <div className="soft-grid absolute inset-0 opacity-45" />
      <div className="absolute -left-28 top-24 h-80 w-80 rounded-full bg-brand-200/35 blur-3xl" />
      <div className="absolute -right-32 bottom-16 h-96 w-96 rounded-full bg-accent-200/30 blur-3xl" />

      <div className="app-container relative">
        <div className={`scroll-reveal scroll-reveal--up ${inView ? "is-visible" : ""} text-center`}>
          <span className="eyebrow"><Play size={14} fill="currentColor" /> Demo chat</span>
          <h2 className="section-title mx-auto mt-5 max-w-4xl">Lihat sendiri. Satu chat jadi website.</h2>
          <p className="mx-auto mt-4 max-w-2xl text-base leading-8 text-slate-500">
            Mulai dengan <strong className="text-slate-800">#CSJW</strong>, lengkapi data usaha, lalu website jualanmu muncul dan siap dibagikan.
          </p>
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-center gap-2 sm:gap-3">
          {["Mulai chat", "Lengkapi data", "Website disiapkan", "Website online"].map((label, index) => {
            const active = phase.index === index;
            const complete = phase.index > index;
            return (
              <div
                key={label}
                className={`chat-demo-phase ${active ? "is-active" : ""} ${complete ? "is-complete" : ""}`}
              >
                <span>{complete ? <Check size={13} strokeWidth={3} /> : index + 1}</span>
                {label}
              </div>
            );
          })}
        </div>

        <div className="chat-demo-stage mt-10">
          <div className="chat-demo-copy">
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-200 bg-brand-50 px-3 py-1.5 text-xs font-black text-brand-700">
              <Sparkles size={14} /> Tanpa formulir yang membingungkan
            </span>
            <h3 className="mt-5 max-w-lg text-3xl font-black tracking-[-.045em] text-slate-950 sm:text-4xl">
              Ceritakan usahamu seperti sedang chat biasa.
            </h3>
            <p className="mt-4 max-w-lg text-sm leading-7 text-slate-600 sm:text-base">
              CSJW memandu data yang dibutuhkan, menyimpannya bertahap, lalu membuat website setelah semuanya lengkap.
            </p>

            <div className="mt-7 space-y-3">
              {demoBenefits.map(({ icon: Icon, title, text }, index) => (
                <article key={title} className={`chat-demo-benefit ${phase.index >= index ? "is-active" : ""}`}>
                  <span><Icon size={19} /></span>
                  <div><h4>{title}</h4><p>{text}</p></div>
                </article>
              ))}
            </div>

            <div className="mt-7 flex flex-wrap gap-3">
              <button type="button" onClick={replay} className="btn-secondary !rounded-xl">
                <RefreshCcw size={17} /> Putar ulang demo
              </button>
            </div>
          </div>

          <div className="chat-demo-device-wrap" aria-live="polite">
            <div className="chat-demo-phone" key={`phone-${runId}`}>
              <div className="chat-demo-phone-speaker" />
              <div className="chat-demo-phone-screen">
                <div className="chat-demo-statusbar">
                  <span>10:45</span>
                  <div className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-slate-900" /><span className="h-2 w-3 rounded-[3px] border border-slate-800" /><span className="h-2 w-4 rounded-[3px] bg-slate-900" /></div>
                </div>
                <div className="chat-demo-header">
                  <span className="grid h-9 w-9 place-items-center overflow-hidden rounded-full bg-white shadow-sm">
                    <img src="/brand/logo-mark-exact.png" alt="CSJW" className="h-full w-full object-cover" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[12px] font-black text-slate-950">CSJW Assistant</p>
                    <p className="text-[9px] font-bold text-brand-600">online</p>
                  </div>
                  <MessageCircleMore size={17} className="text-slate-600" />
                </div>

                <div ref={chatScrollRef} className="chat-demo-chat">
                  <div className="mb-3 text-center"><span className="rounded-full bg-white/70 px-2.5 py-1 text-[8px] font-black text-slate-400 shadow-sm">HARI INI</span></div>

                  {step >= 1 && (
                    <div className="chat-demo-message chat-demo-message--out">
                      <p>#CSJW</p><time>10:41 ✓✓</time>
                    </div>
                  )}

                  {step >= 2 && (
                    <div className="chat-demo-message chat-demo-message--in">
                      <p className="font-extrabold text-slate-900">Siap! Yuk lengkapi data usahamu.</p>
                      <ul className="mt-2 space-y-1 text-[10px] leading-4 text-slate-600">
                        <li>• Nama usaha</li><li>• Nama produk & harga</li><li>• Foto produk</li><li>• QRIS merchant</li>
                      </ul>
                      <time>10:41</time>
                    </div>
                  )}

                  {step >= 3 && (
                    <div className="chat-demo-message chat-demo-message--out chat-demo-message--data">
                      <p><strong>Sambal Bu Rina</strong><br />Sambal Cumi 150 gram<br />Rp35.000</p>
                      <div className="mt-2 flex gap-1.5">
                        <span><ImageIcon size={10} /> Foto produk</span>
                        <span><QrCode size={10} /> QRIS</span>
                      </div>
                      <time>10:42 ✓✓</time>
                    </div>
                  )}

                  {step >= 4 && (
                    <div className="chat-demo-message chat-demo-message--in">
                      <div className="flex items-center gap-2">
                        <span className="grid h-7 w-7 place-items-center rounded-lg bg-brand-50 text-brand-700"><WandSparkles size={14} /></span>
                        <div><p className="text-[10px] font-black text-slate-900">Data sudah lengkap</p><p className="text-[9px] text-slate-500">Website sedang disiapkan...</p></div>
                      </div>
                      {step === 4 && <div className="chat-demo-typing mt-2"><i /><i /><i /></div>}
                      <time>10:43</time>
                    </div>
                  )}

                  {step >= 5 && (
                    <div className="chat-demo-message chat-demo-message--in chat-demo-message--result">
                      <div className="flex items-center gap-2 text-brand-700"><CheckCircle2 size={16} /><strong>Website kamu siap!</strong></div>
                      <div className="mt-2 overflow-hidden rounded-xl border border-brand-100 bg-white">
                        <div className="h-20 overflow-hidden bg-slate-100"><img src="/images/showcase-v16/site-sambal-bu-rina.webp" alt="Preview Sambal Bu Rina" className="h-full w-full object-cover object-top" /></div>
                        <div className="p-2"><p className="text-[9px] font-black text-slate-900">Sambal Bu Rina</p><p className="mt-0.5 truncate text-[8px] text-brand-600">sambalburina.csjw.pages.dev</p></div>
                      </div>
                      <button type="button" className="mt-2 flex w-full items-center justify-center gap-1 rounded-lg bg-brand-600 px-2 py-2 text-[9px] font-black text-white">Buka website <ExternalLink size={10} /></button>
                      <time>10:45</time>
                    </div>
                  )}

                </div>

                <div className="chat-demo-composer">
                  <span>Ketik pesan</span><span className="grid h-8 w-8 place-items-center rounded-full bg-brand-600 text-white"><Send size={13} /></span>
                </div>
              </div>
            </div>

            <div className={`chat-demo-transfer ${step >= 5 ? "is-active" : ""}`} aria-hidden="true">
              <span /><span /><span />
            </div>
          </div>

          <div className={`chat-demo-browser ${step >= 6 ? "is-visible" : ""}`} key={`browser-${runId}`}>
            <div className="chat-demo-browser-top">
              <div className="flex gap-1.5"><span className="bg-red-400" /><span className="bg-amber-400" /><span className="bg-emerald-400" /></div>
              <div className="chat-demo-address"><Globe2 size={12} /> sambalburina.csjw.pages.dev</div>
              <CheckCircle2 size={16} className="text-brand-600" />
            </div>
            <div className="chat-demo-site-window">
              <img src="/images/showcase-v16/site-sambal-bu-rina.webp" alt="Website Sambal Bu Rina yang selesai dibuat" />
              <div className="chat-demo-site-shine" />
            </div>
            <div className="chat-demo-browser-footer">
              <span className="flex items-center gap-2 text-xs font-black text-brand-700"><CheckCircle2 size={16} /> Website berhasil dibuat</span>
              <span className="rounded-full bg-brand-50 px-3 py-1.5 text-[10px] font-black text-brand-700">Siap dibagikan</span>
            </div>
          </div>
        </div>

        <div className="mx-auto mt-8 max-w-3xl">
          <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-[.14em] text-slate-400"><span>{phase.label}</span><span>{progress}%</span></div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-white shadow-inner"><span className="block h-full rounded-full bg-gradient-to-r from-brand-500 via-brand-600 to-accent-500 transition-[width] duration-700 ease-out" style={{ width: `${progress}%` }} /></div>
        </div>
      </div>
    </section>
  );
}
